import { Box, Stack, Typography } from '@mui/material';
import { first } from 'lodash';
import { useMemo } from 'react';
import { Path, useWatch } from 'react-hook-form';

import { Link } from '@/components/form/baseInputs/Link';
import { FormAwareButtonGroup } from '@/components/form/formAwareInputs/FormAwareButtonGroup';
import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormAwareCurrencyInput } from '@/components/form/formAwareInputs/FormAwareCurrencyInput';
import { FormAwareFormattedNumberInput } from '@/components/form/formAwareInputs/FormAwareFormattedNumberInput';
import { FormAwarePercentInput } from '@/components/form/formAwareInputs/FormAwarePercentInput';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { Card } from '@/components/layout/Card/Card';
import { useFormContext } from '@/components/react-hook-form/hooks';
import { useModalState } from '@/hooks/useModalState';
import { ClientProfileSideDrawer } from '@/modules/clientProfiles/ClientProfileForm/ClientProfileModal';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { COLORS } from '@/styles/tokens/colors';
import { EntityKind, ScheduledDistributionKind } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';
import { getPulidKind, PulidKind } from '@/utils/pulid';

import { useEntityDetailsContext } from '../contexts/entityDetails/entityDetails.context';
import {
  SCHEDULED_DISTRIBUTION_FREQUENCY_ITEMS,
  SCHEDULED_DISTRIBUTION_KINDS,
} from './EntityBeneficiariesSubform.constants';
import { BeneficiaryFormScheduledDistribution } from './EntityBeneficiariesSubform.types';
import { useAgeCheckQuery } from './graphql/AgeCheck.generated';
import { GrowByInflationCheckbox } from './GrowByInflationCheckbox';

// The ScheduledDistributionForm is part of the larger EntityBeneficiariesSubform, however
// we use BeneficiaryFormScheduledDistribution as the type for this particular form component
// so it is less coupled with the larger form context. This also helps with TS performance.
function getScheduledDistributionFieldName<
  T extends ScheduledDistributionFormPaths,
>(
  prefix: ScheduledDistributionFormProps['fieldName'],
  scheduledDistributionFormPath: T
): T {
  return `${prefix}.${scheduledDistributionFormPath}` as T;
}

type ScheduledDistributionFormPaths =
  Path<BeneficiaryFormScheduledDistribution>;

interface ScheduledDistributionFormProps {
  fieldName: `scheduledDistributions.${number}`;
  beneficiaryId: string;
}

export function ScheduledDistributionForm({
  fieldName,
  beneficiaryId,
}: ScheduledDistributionFormProps) {
  const { householdId } = useHouseholdDetailsContext();
  const [{ isModalOpen }, { openModal, closeModal }] = useModalState();
  const { control } = useFormContext<BeneficiaryFormScheduledDistribution>();
  const selectedKind = useWatch({
    name: getScheduledDistributionFieldName(
      fieldName,
      'scheduledDistribution.kind'
    ),
    control,
  });

  const hasAgeRequirementToggled = useWatch({
    name: getScheduledDistributionFieldName(fieldName, '_hasAgeParams'),
    control,
  });

  const [clientProfileId, shouldCheckAge] = useMemo<[string, boolean]>(() => {
    if (!hasAgeRequirementToggled) return ['', false];
    const pulidKind = getPulidKind(beneficiaryId);
    if (pulidKind !== PulidKind.ClientProfile) return ['', false];

    return [beneficiaryId, true];
  }, [beneficiaryId, hasAgeRequirementToggled]);
  const {
    data: ageCheckData,
    loading,
    error,
    refetch,
  } = useAgeCheckQuery({
    variables: {
      clientProfileId,
      shouldCheckAge,
    },
  });

  const hasValidAge = useMemo<boolean>(() => {
    if (!shouldCheckAge) return true;
    if (error || loading) return false;

    return !!first(getNodes(ageCheckData?.clientProfiles))?.dateOfBirth;
  }, [ageCheckData?.clientProfiles, error, loading, shouldCheckAge]);

  const selectedAgeParamKind = useWatch({
    name: getScheduledDistributionFieldName(fieldName, '_ageRequirementKind'),
    control,
  });

  const { entityKind } = useEntityDetailsContext();
  const isRetirement = entityKind === EntityKind.RetirementPersonalAccount;

  const scheduledDistributionKindOptions = useMemo(
    () =>
      isRetirement
        ? SCHEDULED_DISTRIBUTION_KINDS.filter(
            (kind) => kind.value !== ScheduledDistributionKind.AllIncome
          )
        : SCHEDULED_DISTRIBUTION_KINDS,
    [isRetirement]
  );

  const isPercentage = selectedKind === ScheduledDistributionKind.Percentage;
  const isAmount = selectedKind === ScheduledDistributionKind.Amount;
  const isTrustIncome = selectedKind === ScheduledDistributionKind.AllIncome;

  return (
    <Card
      variant="filled"
      sx={{ p: 2, bgcolor: COLORS.GRAY[200] }}
      data-testid={`${fieldName}-scheduled-distribution-form`}
    >
      <Stack spacing={2}>
        <Stack
          direction="row"
          spacing={2}
          width="100%"
          alignItems={isTrustIncome ? 'start' : 'center'}
        >
          <FormAwareButtonGroup<BeneficiaryFormScheduledDistribution>
            variant="dark"
            label="Scheduled distribution"
            hideLabel
            fieldName={getScheduledDistributionFieldName(
              fieldName,
              'scheduledDistribution.kind'
            )}
            control={control}
            options={scheduledDistributionKindOptions}
          />
          <Box sx={{ flexGrow: 1, flexShrink: 1, flexBasis: '50%' }}>
            {isPercentage && (
              <FormAwarePercentInput<BeneficiaryFormScheduledDistribution>
                required
                label="Distribution percentage"
                hideLabel
                fieldName={getScheduledDistributionFieldName(
                  fieldName,
                  'scheduledDistribution.percentage'
                )}
                control={control}
                isDecimalJSInput
                decimalScale={2}
              />
            )}
            {isAmount && (
              <FormAwareCurrencyInput<BeneficiaryFormScheduledDistribution>
                required
                label="Distribution amount"
                hideLabel
                fieldName={getScheduledDistributionFieldName(
                  fieldName,
                  'scheduledDistribution.amount'
                )}
                control={control}
                isDecimalJSInput
              />
            )}
            {isTrustIncome && (
              <FormAwarePercentInput<BeneficiaryFormScheduledDistribution>
                required
                label="Distribution percentage"
                hideLabel
                fieldName={getScheduledDistributionFieldName(
                  fieldName,
                  'scheduledDistribution.percentage'
                )}
                control={control}
                isDecimalJSInput
                decimalScale={2}
                helpText="Percentage of trust income per year"
              />
            )}
          </Box>
          {!isTrustIncome && (
            <Box sx={{ flexGrow: 1, flexShrink: 1, flexBasis: '50%' }}>
              <FormAwareSelectInput<BeneficiaryFormScheduledDistribution>
                required
                label="Distribution frequency"
                hideLabel
                fieldName={getScheduledDistributionFieldName(
                  fieldName,
                  'scheduledDistribution.frequency'
                )}
                control={control}
                options={SCHEDULED_DISTRIBUTION_FREQUENCY_ITEMS}
              />
            </Box>
          )}
        </Stack>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <FormAwareCheckbox<BeneficiaryFormScheduledDistribution>
            label="Age requirement"
            fieldName={getScheduledDistributionFieldName(
              fieldName,
              '_hasAgeParams'
            )}
            control={control}
          />
          {isAmount && (
            <GrowByInflationCheckbox
              fieldName={getScheduledDistributionFieldName(
                fieldName,
                'scheduledDistribution.growByInflation'
              )}
            />
          )}
        </Stack>
        {hasAgeRequirementToggled && (
          <Stack spacing={2}>
            <Stack spacing={1} direction="row" alignItems="center">
              <Stack minWidth="120px">
                <FormAwareSelectInput<BeneficiaryFormScheduledDistribution>
                  label="Age requirement"
                  hideLabel
                  fieldName={getScheduledDistributionFieldName(
                    fieldName,
                    '_ageRequirementKind'
                  )}
                  control={control}
                  options={[
                    { value: 'upon', display: 'Upon' },
                    { value: 'until', display: 'Until' },
                    { value: 'between', display: 'Between' },
                  ]}
                />
              </Stack>
              <Typography variant="body-s" width="30px">
                {selectedAgeParamKind === 'between' ? 'ages' : 'age'}
              </Typography>
              {(selectedAgeParamKind === 'upon' ||
                selectedAgeParamKind === 'between') && (
                <Box width="80px">
                  <FormAwareFormattedNumberInput<BeneficiaryFormScheduledDistribution>
                    control={control}
                    label="Age"
                    hideLabel
                    fieldName={getScheduledDistributionFieldName(
                      fieldName,
                      'scheduledDistribution.ageRequirementStart'
                    )}
                    fixedDecimalScale
                    decimalScale={0}
                    required
                  />
                </Box>
              )}
              {selectedAgeParamKind === 'between' && (
                <Typography variant="body-s">to</Typography>
              )}
              {(selectedAgeParamKind === 'until' ||
                selectedAgeParamKind === 'between') && (
                <Box width="80px">
                  <FormAwareFormattedNumberInput<BeneficiaryFormScheduledDistribution>
                    control={control}
                    label="Age"
                    hideLabel
                    fieldName={getScheduledDistributionFieldName(
                      fieldName,
                      'scheduledDistribution.ageRequirementEnd'
                    )}
                    fixedDecimalScale
                    decimalScale={0}
                    required
                  />
                </Box>
              )}
            </Stack>
            {!hasValidAge && !loading && (
              <Stack spacing={1} direction="row" alignItems="center">
                <Typography variant="body-xs">
                  This person does not have a date of birth specified
                </Typography>
                <Link display="Add DOB" onClick={openModal} />
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
      {isModalOpen && householdId && (
        <ClientProfileSideDrawer
          isOpen={isModalOpen}
          setIsOpen={(isOpen) => {
            if (isOpen) {
              openModal();
            } else {
              void refetch();
              closeModal();
            }
          }}
          clientProfileId={clientProfileId}
          clientProfileTypeDisplay="Beneficiary"
          householdId={householdId}
        />
      )}
    </Card>
  );
}
